/**
 * Manifiesto de módulo: lo que un módulo declara al núcleo para poder enchufarse.
 * El núcleo no conoce el dominio del módulo; solo estas claves, con las que la consola arma planes.
 */
import { z } from "zod";
import { PATTERNS, WEBHOOK_EVENTS } from "./constants.js";
export const ModuleRoleSchema = z.object({
    code: z.string().regex(PATTERNS.KEY),
    name: z.string().min(1).max(80),
    description: z.string().max(400).optional(),
    /**
     * Marca a quien administra la organización. Dentro del módulo, corta el RBAC. Fuera de él,
     * habilita gestionar los miembros de esa organización en el núcleo —invitar, cambiar roles de
     * cualquier módulo, suspender— porque quien administra una empresa la administra entera, no un
     * módulo suelto (ADR-007).
     */
    isAdmin: z.boolean().default(false),
});
export const FeatureValueTypeSchema = z.enum(["boolean", "number", "string"]);
export const ModuleFeatureSchema = z.object({
    key: z.string().regex(PATTERNS.KEY),
    name: z.string().min(1).max(80),
    description: z.string().max(400).optional(),
    valueType: FeatureValueTypeSchema.default("boolean"),
});
/**
 * `gauge`: valor absoluto vigente (asientos ocupados, sedes activas).
 * `counter_monthly`: acumulado que se reinicia cada mes (casos creados).
 */
export const LimitAggregationSchema = z.enum(["gauge", "counter_monthly"]);
export const ModuleLimitSchema = z.object({
    key: z.string().regex(PATTERNS.KEY),
    name: z.string().min(1).max(80),
    unit: z.string().min(1).max(24),
    aggregation: LimitAggregationSchema.default("gauge"),
    description: z.string().max(400).optional(),
});
export const ModuleManifestSchema = z.object({
    code: z.string().regex(PATTERNS.MODULE_CODE),
    name: z.string().min(1).max(80),
    description: z.string().max(400).optional(),
    /** URL pública del módulo, usada por el launcher. */
    baseUrl: z.url(),
    roles: z.array(ModuleRoleSchema).min(1).max(32),
    features: z.array(ModuleFeatureSchema).max(64).default([]),
    limits: z.array(ModuleLimitSchema).max(64).default([]),
    /** Eventos que el módulo desea recibir por webhook. */
    webhookEvents: z.array(z.enum(WEBHOOK_EVENTS)).default([]),
})
    .refine((m) => m.roles.some((r) => r.isAdmin), {
    message: "El manifiesto debe declarar al menos un rol con isAdmin = true.",
    path: ["roles"],
})
    .refine((m) => new Set(m.roles.map((r) => r.code)).size === m.roles.length, {
    message: "Códigos de rol duplicados.",
    path: ["roles"],
})
    .refine((m) => new Set(m.features.map((f) => f.key)).size === m.features.length, {
    message: "Claves de feature duplicadas.",
    path: ["features"],
})
    .refine((m) => new Set(m.limits.map((l) => l.key)).size === m.limits.length, {
    message: "Claves de límite duplicadas.",
    path: ["limits"],
});
//# sourceMappingURL=manifest.js.map